import { Box } from "@/components/essentials/box";
import { Button } from "@/components/essentials/button";
import { Typography } from "@/components/essentials/typography";
import { type ApplicationFormValue } from "@/components/fragments/argyle";
import { BillingForm, billingFormId } from "@/components/fragments/argyle/billing-form";
import { ChildTable } from "@/components/fragments/argyle/child-table";
import { ContractorTable } from "@/components/fragments/argyle/contractor-table";
import { type PlanData, PlanDataTable } from "@/components/fragments/argyle/plan-data-table";
import { ShipmentTable } from "@/components/fragments/argyle/shipment-table";
import { stripeFormId } from "@/components/fragments/stripe/stripe-form/const";
import { Stack } from "@chakra-ui/layout";
import { type SetupIntentResult, type StripePaymentElementChangeEvent } from "@stripe/stripe-js";
import { type FC, useCallback, useState } from "react";
import { type CustomerSlot } from "@/pages/signup/plan/[priceUuid]/__components__/input-screen/";
import {
  confirmScreenButtonCancelTestId,
  confirmScreenButtonSubmitTestId,
  confirmScreenSpecialNoteTestId,
  confirmScreenTestId,
  confirmScreenTypographyDescriptionTestId,
  confirmScreenTypographyTitleTestId,
} from "./const";
import { ConfirmMessageBox } from "./__components__/confirm-message-box/ConfirmMessageBox";
import { Toast, useToast } from "@/components/essentials/toast";
import { useCreateCustomerPagesSignupPlanMutation } from "./__gql__/create-customer.gen";
import { getCreateCustomerInput } from "./module";
import { useApolloError } from "@/hooks/useApolloError";
import * as Scroll from "react-scroll";
import { scrollOffset } from "@/pages/plans/detail/[shipmentId]/const";
import { isNonNullable } from "@/modules/utils/predicate";
import { useStripeFormState } from "./hook";
import { datadogLogs } from "@datadog/browser-logs";
import { useCompletePlanApplicationPagesSignupPlanMutation } from "./__gql__/complete-plan-application.gen";
import { KonamiSportsTable } from "@/components/fragments/argyle/konami-sports-table";

export interface ConfirmScreenProps {
  readonly planData: PlanData;
  readonly promotionCode: string | undefined;
  readonly applicationFormValue: ApplicationFormValue;
  readonly customerSlot: CustomerSlot;
  readonly onCompletedSubmit: () => void;
  readonly onClickBackButton: () => void;
  readonly headerMessage: string;
  readonly isKonamiSports?: boolean;
}

export const ConfirmScreen: FC<ConfirmScreenProps> = ({
  planData,
  promotionCode,
  applicationFormValue,
  customerSlot,
  onCompletedSubmit,
  onClickBackButton,
  headerMessage,
  isKonamiSports,
}) => {
  const toast = useToast();
  const [stripeFormState, setStripeFormState] = useStripeFormState();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [createCustomer, resultCreateCustomer] = useCreateCustomerPagesSignupPlanMutation();
  const [completePlanApplication] = useCompletePlanApplicationPagesSignupPlanMutation();

  const onSubmit = useCallback(
    async (confirmSetup: () => Promise<SetupIntentResult | undefined>) => {
      setIsSubmitting(true);
      const failedSubmit = ({ scrollStripeForm = true }: { scrollStripeForm?: boolean }): void => {
        if (scrollStripeForm) {
          Scroll.scroller.scrollTo(billingFormId, {
            smooth: false,
            duration: 0,
            offset: scrollOffset,
          });
        }
        setIsSubmitting(false);
      };

      try {
        if (stripeFormState === undefined || !stripeFormState.complete) {
          toast({
            position: "bottom",
            render: () => <Toast variant="error">決済情報を入力してください。</Toast>,
          });
          failedSubmit({});
          return;
        }

        if (isKonamiSports) {
          applicationFormValue.specialNote +=
            "\n-----コナミスポーツクラブ会員情報-----\n施設名：" +
            (applicationFormValue.konamiSports?.shisetsu ?? "") +
            "\nコナミスポーツクラブ会員番号：" +
            (applicationFormValue.konamiSports?.memberCode ?? "");
        }

        const resultCreateCustomer = await createCustomer({
          variables: {
            customerId: customerSlot.id,
            input: getCreateCustomerInput(applicationFormValue, promotionCode, planData),
          },
          // catch すると結果が void になるし、onError を指定しないとエラー時に処理が終わる
          onError(error) {
            error.graphQLErrors.forEach((error) => {
              datadogLogs.logger.error(
                `An error occurred in BillingForm: Argyle customerId=${
                  customerSlot.id
                } createCustomer error, errorMessage=${error.message} errorExtensions=${JSON.stringify(
                  error.extensions,
                )} `,
              );
            });
          },
        });

        if (isNonNullable(resultCreateCustomer.errors)) {
          failedSubmit({ scrollStripeForm: false });
          return;
        }

        const confirmSetupResult = await confirmSetup();
        if (confirmSetupResult === undefined) {
          datadogLogs.logger.error(
            `An error occurred in BillingForm: Argyle customerId=${customerSlot.id} confirmSetup return undefined`,
          );

          toast({
            position: "bottom",
            render: () => <Toast variant="error">決済情報の登録に失敗しました</Toast>,
          });
          failedSubmit({});
          return;
        }

        const { setupIntent, error } = confirmSetupResult;

        if (setupIntent?.status !== "succeeded") {
          if (isNonNullable(error)) {
            datadogLogs.logger.error(
              `An error occurred in BillingForm: Argyle customerId = ${customerSlot.id}, error code = ${
                error.code ?? "undefined(not provided form Stripe)"
              }, error message = ${error.message ?? "undefined(not provided form Stripe)"}`,
            );

            toast({
              position: "bottom",
              render: () => <Toast variant="error">{error.message ?? "決済情報の登録に失敗しました"}</Toast>,
            });
          } else {
            toast({
              position: "bottom",
              render: () => <Toast variant="error">決済情報の登録に失敗しました</Toast>,
            });
          }
          failedSubmit({});
          return;
        }

        // あえてエラー処理しない。
        // 現状バックエンドの処理はメール通知のみなので、ここで失敗してもクリティカルな問題はでないため、通常通り完了画面を表示する。
        // biome-ignore lint/complexity/noVoid :[AUTOMATICALLY-INSERTED] The use of void is not allowed.
        void completePlanApplication({
          variables: { customerId: customerSlot.id },
          onError(error) {
            error.graphQLErrors.forEach((error) => {
              datadogLogs.logger.error(
                `An error occurred in BillingForm: Argyle customerId = ${
                  customerSlot.id
                } completePlanApplication error!, errorMessage=${error.message} errorExtensions=${JSON.stringify(
                  error.extensions,
                )} `,
              );
            });
          },
        });

        onCompletedSubmit();
      } catch {
        failedSubmit({});
      }
    },
    [
      applicationFormValue,
      completePlanApplication,
      createCustomer,
      customerSlot.id,
      isKonamiSports,
      onCompletedSubmit,
      planData,
      promotionCode,
      stripeFormState,
      toast,
    ],
  );

  const onChange = (event: StripePaymentElementChangeEvent): void => {
    setStripeFormState(event);
  };

  useApolloError(resultCreateCustomer.error);

  return (
    <Stack px="4" spacing="10" my="10" data-testid={confirmScreenTestId}>
      <Box wordBreak="break-word" textAlign="center">
        <Typography variant="display3" textAlign="center" color="secondary.main" mb="4">
          {headerMessage}
        </Typography>
        <Typography
          variant="display3"
          textAlign="center"
          color="mono.dark.87"
          data-testid={confirmScreenTypographyTitleTestId}
        >
          内容の確認とお支払い情報の入力
        </Typography>
        <Typography
          variant="text4"
          mt="0.25rem"
          mx="4"
          textAlign="center"
          color="mono.dark.54"
          data-testid={confirmScreenTypographyDescriptionTestId}
        >
          内容をご確認のうえ、お支払い情報を入力してください。
        </Typography>
      </Box>
      <PlanDataTable data={planData} promotionCode={promotionCode} />
      <ContractorTable value={applicationFormValue} />
      <ShipmentTable value={applicationFormValue} />
      <ChildTable value={applicationFormValue} />
      <Box>
        <Typography variant="display6" mb="2">
          その他備考
        </Typography>
        <Box background="background.main" mt="3" p="4" borderRadius="8" data-testid={confirmScreenSpecialNoteTestId}>
          <Typography variant="text4" whiteSpace="pre">
            {applicationFormValue.specialNote === "" ? "入力なし" : applicationFormValue.specialNote}
          </Typography>
        </Box>
      </Box>

      {isKonamiSports && <KonamiSportsTable value={applicationFormValue} />}
      <BillingForm clientSecret={customerSlot.clientSecret} onSubmit={onSubmit} onChange={onChange} />
      <ConfirmMessageBox planData={planData} promotionCode={promotionCode} />
      <Stack spacing="5" pt="10">
        <Button
          size="lg"
          width="100%"
          type="submit"
          disabled={isSubmitting}
          isLoading={isSubmitting}
          form={stripeFormId}
          data-testid={confirmScreenButtonSubmitTestId}
        >
          上記の内容で申し込む
        </Button>
        <Button
          styleVariant="mono"
          size="lg"
          width="100%"
          disabled={isSubmitting}
          data-testid={confirmScreenButtonCancelTestId}
          onClick={onClickBackButton}
        >
          内容を修正する
        </Button>
      </Stack>
    </Stack>
  );
};
